import { useState, useEffect } from 'react';
import { RecordDrawer } from '../../../components/ui/RecordDrawer';
import type { RoleDefinition } from '../../../app/roles';
import type { AppRepositories, Asset } from '../../../domain';
import { PerspectiveFieldGuide, useDepth } from '../../../presentation';
import { MaskedValue } from '../phone-detail/MaskedValue';

export function AssetDrawer({ assetId, onClose, role, repo, onViewDetails }: { assetId: string | null, onClose: () => void, role: RoleDefinition, repo: AppRepositories, onViewDetails: (id: string) => void }) {
  const { depth } = useDepth();
  const [asset, setAsset] = useState<Asset | null>(null);

  useEffect(() => {
    if (assetId) {
      repo.inventory.getAsset(assetId).then(setAsset);
    } else {
      setAsset(null);
    }
  }, [assetId, repo]);

  return (
    <RecordDrawer
      isOpen={!!assetId}
      onClose={onClose}
      eyebrow="Mobile Asset"
      title={asset ? `${asset.assetTag} - ${asset.name}` : 'Loading...'}
    >
      {asset && (
        <div className="asset-drawer-content">
          <PerspectiveFieldGuide contextLabel="Selected asset fields" surface="assets" />
          {depth === 'overview' && (
            <>
              <div className="drawer-section-heading">Device outcome</div>
              <div className="drawer-evidence">
                <span><strong>Current state:</strong> {asset.state}</span>
                <span><strong>Condition:</strong> Good</span>
                <span><strong>Last verified:</strong> 2 days ago</span>
                <span><strong>Next action:</strong> Review assignment and warranty</span>
              </div>
            </>
          )}
          {depth === 'operations' && (
            <>
              <div className="drawer-section-heading">MDM Metrics</div>
              <div className="drawer-evidence">
                <span><strong>Carrier:</strong> Cascade Telecom</span>
                <span><strong>Last Check-in:</strong> 2 mins ago</span>
                <span><strong>OS Version:</strong> 17.4.1 (Compliant)</span>
                <span><strong>Battery Health:</strong> 88%</span>
              </div>
              <div className="drawer-section-heading">Hardware</div>
              <div className="drawer-evidence">
                <span><strong>Serial:</strong> <MaskedValue value={asset.serialNumber || 'N/A'} role={role} type="serial" /></span>
                <span><strong>IMEI:</strong> <MaskedValue value="358921008891234" role={role} type="imei" /></span>
                <span><strong>Purchase Date:</strong> {asset.purchaseDate || 'Unknown'}</span>
              </div>
            </>
          )}
          {depth === 'architecture' && (
            <>
              <div className="drawer-section-heading">Record relationships</div>
              <div className="drawer-evidence">
                <span><strong>Asset ID:</strong> {asset.id}</span>
                <span><strong>Serial:</strong> <MaskedValue value={asset.serialNumber || 'N/A'} role={role} type="serial" /></span>
                <span><strong>IMEI:</strong> <MaskedValue value="358921008891234" role={role} type="imei" /></span>
                <span><strong>Assignment edge:</strong> asset.id → assignment.assetId</span>
                <span><strong>Location edge:</strong> asset.locationId → room.id</span>
                <span><strong>Source:</strong> InventoryRepository</span>
              </div>
            </>
          )}

          <div className="drawer-actions">
            <button className="button button--primary" disabled={!role.canEditTechnicalRecords}>Edit Device</button>
            <button className="button" disabled={!role.canEditTechnicalRecords}>Reassign</button>
            <button className="button" onClick={() => onViewDetails(asset.id)}>View Details</button>
          </div>
        </div>
      )}
    </RecordDrawer>
  );
}
